/**
 * PRODUCTION FIX: Error boundary for async operations and promise rejections
 * Extends base ErrorBoundary with unhandled promise rejection handling
 */

import React, { ReactNode, useEffect, useState, useCallback } from 'react';
import { normalizeCaughtError } from '@/shared/utils/errorHandler';
import { ErrorBoundary } from './ErrorBoundary';
import { log } from '@/shared/utils/logger';
import {
  scheduleDynamicImportRecoveryGuardClear,
  tryRecoverFromDynamicImportFailure,
} from '@/shared/utils/dynamicImportRecovery';

interface AsyncErrorBoundaryProps {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: React.ErrorInfo | null, reset: () => void) => ReactNode;
  onError?: (error: Error, errorInfo?: React.ErrorInfo) => void;
  resetKeys?: Array<string | number>;
  captureAsyncAsFatal?: boolean;
}

function AsyncErrorThrower({ error }: { error: Error }): JSX.Element {
  throw error;
}

/**
 * Error boundary that catches both React errors and unhandled promise rejections
 */
export function AsyncErrorBoundary({
  children,
  fallback,
  onError,
  resetKeys,
  captureAsyncAsFatal = false,
}: AsyncErrorBoundaryProps): JSX.Element {
  const [asyncError, setAsyncError] = useState<Error | null>(null);

  // Handle unhandled promise rejections
  const handlePromiseRejection = useCallback(
    (event: PromiseRejectionEvent) => {
      const error = normalizeCaughtError(event.reason);
      log.error('AsyncErrorBoundary', 'Unhandled promise rejection:', error);

      if (tryRecoverFromDynamicImportFailure(error)) {
        event.preventDefault();
        return;
      }

      if (captureAsyncAsFatal) {
        setAsyncError(error);
      } else if (onError) {
        onError(error);
      }

      // Prevent default browser behavior (console error)
      event.preventDefault();
    },
    [captureAsyncAsFatal, onError]
  );

  // Handle uncaught errors
  const handleError = useCallback(
    (event: ErrorEvent) => {
      const error = normalizeCaughtError(event.error ?? event.message);
      log.error('AsyncErrorBoundary', 'Uncaught error:', error);

      if (tryRecoverFromDynamicImportFailure(error)) {
        event.preventDefault();
        return;
      }

      if (captureAsyncAsFatal) {
        setAsyncError(error);
      } else if (onError) {
        onError(error);
      }

      // Prevent default browser behavior
      event.preventDefault();
    },
    [captureAsyncAsFatal, onError]
  );

  // Register global error handlers
  useEffect(() => {
    const recoveryGuardTimer = scheduleDynamicImportRecoveryGuardClear();

    window.addEventListener('unhandledrejection', handlePromiseRejection);
    window.addEventListener('error', handleError);

    return () => {
      if (recoveryGuardTimer !== undefined) {
        window.clearTimeout(recoveryGuardTimer);
      }
      window.removeEventListener('unhandledrejection', handlePromiseRejection);
      window.removeEventListener('error', handleError);
    };
  }, [handlePromiseRejection, handleError]);

  // Custom error handler that includes async errors
  const handleErrorBoundaryError = useCallback(
    (error: Error, errorInfo: React.ErrorInfo) => {
      log.error('AsyncErrorBoundary', 'React error:', error, errorInfo);

      // Once the nested boundary owns the error, it is safe to clear the
      // transient value. This lets its reset callback retry the children.
      setAsyncError((currentError) => currentError === error ? null : currentError);

      if (onError) {
        onError(error, errorInfo);
      }
    },
    [onError]
  );

  // Throw from a descendant so the ErrorBoundary can catch fatal async errors.
  // Calling a throwing expression while constructing ErrorBoundary's children
  // would throw from this component, outside the boundary being returned.
  return (
    <ErrorBoundary
      fallback={fallback}
      onError={handleErrorBoundaryError}
      resetKeys={resetKeys}
    >
      {asyncError ? <AsyncErrorThrower error={asyncError} /> : children}
    </ErrorBoundary>
  );
}

/**
 * Default export for convenience
 */
export default AsyncErrorBoundary;
